/**
 * 中身のURLをリンク化
 * 既にaタグがあれば無視（一部だけURLのみ、は想定しない）
 * @param text 
 * @returns リンク化（aタグに変換）したtext
 */
export function linkify(text: string): string {
  const urlPattern = /(https?:\/\/[^\s]+)/g

  return text.replace(urlPattern, (url) => {
    // 既にaタグがあれば無視（一部だけURLのみ、は想定しない）
    if (text.includes('<a')) {
      return url
    } else {
      return `<a href="${url}" target="_blank">${url}</a>`
    }
  })
}

/**
 * 所定のフォーマットを受け取り、htmlタグを返す
 * formatはマスタッシュの中にポイント情報のキーを入れると、置き換えて返される
 *
 * @param point ポイント
 * @param settings {
 *   type: text（デフォルト） | link | tel | postal | mail | boolean | | distance | func,
 *   format: "任意のテキスト{{pointのkey}}任意のテキスト"
 *   key: Pointのフィールドのキー、複雑な書式が不要ならこちらを使う
 * }
 *
 * ※ 距離を表示するには、先に現在地を取得しておく必要がある
 *
 * @returns タグ または type = textの場合はテキスト, nullは項目ごと消したい時（未設定・もしくは隠す条件に該当、空文字と明示的に区別）
 */
export function parse(point: Point, settings: PointFormat): string {
  if (!settings) {
    return null
  }

  // 高度な設定のパターンを適用
  let advancedValue = null
  if (settings.kind === 'advanced') {
    for (const pattern of settings.patterns || []) {
      // 条件を満たすか判定
      const passedOrNot: boolean[] = []
      pattern.conditions.forEach(condition => {
        let originalValue = point[condition.key as keyof Point] || point.extra_fields[condition.key]
        if (originalValue == null) {
          originalValue = ''
        }

        let conditionValue = condition.value
        if (conditionValue == null) {
          conditionValue = ''
        }

        // console.debug('pattern condition', condition, originalValue, conditionValue)

        if (condition.comparison === '=') {
          passedOrNot.push(conditionValue == originalValue)
        } else if (condition.comparison === '!=') {
          passedOrNot.push(conditionValue != originalValue)
        }
      })

      // AND: 全て満たす / OR: いずれか満たす 必要ある
      if (pattern.operation === 'AND' && !passedOrNot.every(v => v)) {
        continue
      } else if (pattern.operation === 'OR' && !passedOrNot.some(v => v)) {
        continue
      }

      // 隠すものなら、空として決定
      if (pattern.hide) {
        // console.debug('pattern hide', pattern)
        return null
      }

      // 最初に条件を満たしたものを使う
      // console.debug('pattern use value', pattern.value_format)
      advancedValue = pattern.value_format
      break
    }
  }

  let value = advancedValue

  // 高度な設定でパターンを使ったもの以外
  if (value == null) {
    value = settings.key
      ? (point[settings.key as keyof Point] || point.extra_fields[settings.key]) as string // TODO 0を救えるように
      : settings.format?.replace(/\n/g, "<br>")
  }

  // 比較ブレ防止・空対策
  if (value == null) {
    value = ''
  }

  // 表記反映
  Object.keys(storelocator.messages)
  .filter(key => value.includes(`{{messages.${key}}}`))
  .forEach(key => {
    value = value.replace(`{{messages.${key}}}`, storelocator.messages[key])
  })

  // 距離
  let distance: string
  const isDistance = settings.format && settings.format.includes("{{distance}}")
  if (isDistance) {
    distance = mToKm(point.distance)
    if (!distance) {
      return ''
    }  
  }

  value = value.replaceAll(/{{(.+?)}}/g, (match, key: string) => {
    if (isDistance) {
      return distance
    }

    if (!point?.extra_fields) {
      return key
    }

    return point.extra_fields[key] != null
      ? point.extra_fields[key] as string
      : point[key as keyof Point] as string
  })

  // 挿入後も空なら、空で確定
  if (value == undefined || value == null || value == "" || value == "null" || value == "undefined") {
    return ''
  }

  value = linkify(value)

  // リンク化や書式調整
  let result
  if (settings.kind === "link") {
    result = `<a href="${value}" target="_blank">${value}</a>`
  // いったんやめる、使っていてぎょっとするので、電話の方法は別に用意する、一応残しておく
  // } else if(settings.kind === "tel") {
  //   result = `<a href="tel:${value}">${value}</a>`
  } else if(settings.kind === "mail") {
    result = `<a href="mailTo:${value}">${value}</a>`
  } else if (settings.kind === "postal") {
    result = postal(value)
  } else {
    result = value
  }

  // 改行コードを置き換え
  if (result) {
    result = result.replace(/\n/g, "<br>")
  }

  return result
}

/**
 * m を km に変換
 * @param {Number} m 
 * @returns km
 */
export function mToKm(m) {
  if (!m) {
    return ''
  }

  if (m < 10) {
    return ''
  }

  if (m < 1000) {
    return `${Math.round(m)} m`
  }

  const km =  (Math.round(m / 100)) / 10
  return `${km} km`
}

/**
 * 郵便番号フォーマット
 * @param {String} code 郵便番号いりの文字列
 * @returns 〒000-0000の形
 */
function postal(code) {
  code = code.replace(/(.*)([0-9０-９]{3,3})([0-9０-９]{4,4})/, "$1$2-$3")
  if (!/〒/.test(code)) {
    code = "〒" + code
  }
  return code
}